import { useEffect, useState } from "react";
import { playerService } from "../../services/api";
import { formatGroupedNumber } from "../../utils/currencyFormatter";
import { useNavigate } from "react-router-dom";

const PlayerTransfers = ({ playerId }) => {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTransfers = async () => {
      if (!playerId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await playerService.getPlayerTransfers(playerId);

        if (response.success && response.data) {
          // Sort transfers by date in reverse order (most recent first)
          const sortedTransfers = [...response.data].sort((a, b) => {
            return new Date(b.date) - new Date(a.date);
          });
          setTransfers(sortedTransfers);
        } else {
          setError(response.error || "Failed to fetch transfers");
          setTransfers([]);
        }
      } catch (err) {
        console.error("Error fetching transfers:", err);
        setError("Failed to fetch transfers");
        setTransfers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTransfers();
  }, [playerId]);

  const formatTransferAmount = (amount) => {
    if (!amount || amount === 0) return "-";

    if (amount >= 1000000) {
      return `€${(amount / 1000000).toFixed(2)} m`;
    } else if (amount >= 1000) {
      return `€${(amount / 1000).toFixed(0)}K`;
    } else {
      return `€${formatGroupedNumber(amount)}`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const getSeasonFromDate = (dateString) => {
    if (!dateString) return "-";

    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = date.getMonth();

      // Football season typically runs from August to May
      if (month >= 7) {
        // August onwards
        return `${year.toString().slice(-2)}/${(year + 1)
          .toString()
          .slice(-2)}`;
      } else {
        // January to July
        return `${(year - 1).toString().slice(-2)}/${year
          .toString()
          .slice(-2)}`;
      }
    } catch {
      return "-";
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-[20px] border border-[#D6DBEA] overflow-hidden">
        <div className="flex items-center justify-center py-16">
          <div className="text-center p-8">
            <div className="text-gray-400 text-lg">Loading transfers...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-[20px] border border-[#D6DBEA] overflow-hidden">
        <div className="flex items-center justify-center py-16">
          <div className="text-center p-8">
            <div className="text-gray-400 text-6xl mb-4">⚠️</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Error Loading Transfers
            </h3>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!transfers || transfers.length === 0) {
    return (
      <div className="bg-white rounded-[20px] border border-[#D6DBEA] overflow-hidden">
        <div className="flex items-center justify-center py-16">
          <div className="text-center p-8">
            <div className="text-gray-400 text-6xl mb-4">📋</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Transfer History
            </h3>
            <p className="text-gray-600">
              No transfer records found for this player.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[20px] border border-[#D6DBEA] overflow-hidden">
      <div className="overflow-x-auto">
        <div className="min-w-[720px]">
          {/* Table Header */}
          <div className="bg-[#F0F0F2] border-b border-[rgba(214,219,234,0.3)] px-4 py-4">
            <div className="grid grid-cols-6 gap-3">
              <div className="text-[#1A212F] text-xs sm:text-sm lg:text-base font-bold leading-[19.5px] uppercase tracking-wide whitespace-nowrap">
                Season
              </div>
              <div className="text-[#1A212F] text-xs sm:text-sm lg:text-base font-bold leading-[19.5px] uppercase tracking-wide whitespace-nowrap">
                Date
              </div>
              <div className="text-[#1A212F] text-xs sm:text-sm lg:text-base font-bold leading-[19.5px] uppercase tracking-wide whitespace-nowrap">
                Left Club
              </div>
              <div className="text-[#1A212F] text-xs sm:text-sm lg:text-base font-bold leading-[19.5px] uppercase tracking-wide whitespace-nowrap">
                Joined Club
              </div>

              <div className="text-[#1A212F] text-xs sm:text-sm lg:text-base font-bold leading-[19.5px] uppercase tracking-wide whitespace-nowrap">
                Transfer Fee
              </div>

              <div className="text-[#1A212F] text-xs sm:text-sm lg:text-base font-bold leading-[19.5px] uppercase tracking-wide whitespace-nowrap">
                Type
              </div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-[#D6DBEA]">
            {transfers.map((transfer, index) => (
              <div
                key={transfer.transfer_id || index}
                className="px-4 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="grid grid-cols-6 gap-3 items-center">
                  {/* Season */}
                  <div className="text-[#222A35] text-xs sm:text-sm lg:text-base font-medium leading-[19.2px] whitespace-nowrap">
                    {getSeasonFromDate(transfer.date)}
                  </div>

                  {/* Date */}
                  <div className="text-[#222A35] text-xs sm:text-sm lg:text-base font-medium leading-[19.2px] whitespace-nowrap">
                    {formatDate(transfer.date)}
                  </div>

                  {/* Left Club */}
                  <div
                    onClick={() => {
                      if (transfer?.from_team_id) {
                        navigate(`/search/club/${transfer?.from_team_id}`);
                      }
                    }}
                    className="flex items-center cursor-pointer gap-2 sm:gap-3"
                  >
                    {transfer.from_team_image_url && (
                      <img
                        src={transfer.from_team_image_url}
                        alt={transfer.from_team}
                        className="w-6 h-6 object-contain rounded-full flex-shrink-0"
                      />
                    )}
                    <span className="text-[#222A35] text-xs sm:text-sm lg:text-base font-medium leading-[19.5px] whitespace-nowrap max-w-[120px] sm:max-w-none truncate">
                      {transfer.from_team || "-"}
                    </span>
                  </div>

                  {/* Joined Club */}
                  <div
                    onClick={() => {
                      if (transfer?.to_team_id) {
                        navigate(`/search/club/${transfer?.to_team_id}`);
                      }
                    }}
                    className="flex items-center cursor-pointer gap-2 sm:gap-3"
                  >
                    {transfer.to_team_image_url && (
                      <img
                        src={transfer.to_team_image_url}
                        alt={transfer.to_team}
                        className="w-6 h-6 object-contain rounded-full flex-shrink-0"
                      />
                    )}
                    <span className="text-[#222A35] text-xs sm:text-sm lg:text-base font-medium leading-[19.5px] whitespace-nowrap max-w-[120px] sm:max-w-none truncate">
                      {transfer.to_team || "-"}
                    </span>
                  </div>

                  {/* Transfer Fee */}
                  <div className="text-[#222A35] text-xs sm:text-sm lg:text-base font-bold leading-[19.2px] whitespace-nowrap">
                    {formatTransferAmount(transfer.fee_amount)}
                  </div>

                  <div className="text-[#222A35] text-xs sm:text-sm lg:text-base font-medium leading-[19.5px] whitespace-nowrap">
                    {transfer.type || "-"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerTransfers;
